import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    ConfirmModal,
    ICard,
    IOrganisation,
    ISupportCase,
    ISupportCaseListItem,
    Page,
    PermissionKeys,
    SortOrder,
    SupportCaseSupportees,
    Table,
    useHasPermission,
    useQueryParamsContext,
    useSelectedOrganisation,
} from '../../../shared';
import { SearchInput } from '../../../shared/components/search/search.component';
import { useDeleteSupportCase, useGetSupportCases } from '../../hooks';
import * as Sentry from '@sentry/react';

export function SupportCasesPage() {
    const { hasPermission } = useHasPermission();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { organisation } = useSelectedOrganisation();
    const {
        queryParams: { page, pageSize, sort, search, filter },
        debouncedQueryParams,
        setFilter,
        setPage,
        setPageSize,
        setSort,
        setSearch,
    } = useQueryParamsContext('support-cases', {
        sort: [{ field: 'lastName, firstName', sort: 'asc' }],
    });

    const {
        isPending,
        data: supportCases,
        error,
    } = useGetSupportCases(
        {
            page,
            pageSize,
            sortBy: sort[0].field.split(', '),
            sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
            search: debouncedQueryParams.search.replaceAll(/  +/g, ' ').trim(),
        },
        window.carity.environment.shareSupportCases || !!organisation?.id,
        !(window.carity.environment.shareSupportCases && !filter.onlyMySupportCases) ? organisation?.id : undefined,
    );

    useEffect(() => {
        if (error) {
            Sentry.captureException(error, { data: supportCases });
        }
    }, [error, supportCases]);

    const { mutate: deleteSupportCase } = useDeleteSupportCase();

    const columns: GridColDef[] = [
        {
            field: 'caseNumber',
            headerName: t('caseNumberLabel'),
            minWidth: 160,
            flex: 0.5,
        },
        {
            field: 'cards',
            headerName: t('card'),
            minWidth: 100,
            flex: 0.5,
            sortable: false,
            valueFormatter: (value: ICard[]) => (value?.length > 1 ? t('multipleCards') : value[0]?.code),
        },
        {
            field: 'lastName, firstName',
            headerName: t('nameLabel'),
            minWidth: 200,
            flex: 1,
            renderCell: ({ row }: GridCellParams<ISupportCase>) => <SupportCaseSupportees supportCase={row} />,
        },
        {
            field: 'numberOfSupportees',
            headerName: t('numberOfSupportees'),
            minWidth: 50,
            flex: 0.5,
            valueGetter: (_, { supportees }: ISupportCaseListItem) => supportees.length,
        },
        ...(window.carity.environment.shareSupportCases
            ? [
                  {
                      field: 'organisations',
                      headerName: t('organisation'),
                      sortable: false,
                      minWidth: 100,
                      flex: 1,
                      valueFormatter: (value: IOrganisation[]) => value.map(({ name }) => name).join(', '),
                  },
              ]
            : []),
        ...(hasPermission(PermissionKeys.SUPPORT_CASES_WRITE)
            ? ([
                  {
                      field: 'actions',
                      headerName: t('actions'),
                      width: 70,
                      align: 'right',
                      sortable: false,
                      renderCell: ({ row: { id } }: GridCellParams<ISupportCase>) => (
                          <>
                              <ConfirmModal
                                  onConfirm={() => deleteSupportCase(id)}
                                  button={
                                      <IconButton aria-label="delete">
                                          <DeleteOutlineIcon />
                                      </IconButton>
                                  }
                                  title={t('supportCaseDeleteWarningTitle')}
                                  text={t('supportCaseDeleteWarningText')}
                              />
                          </>
                      ),
                  },
              ] as GridColDef[])
            : []),
    ];

    return (
        <Page
            title={t('supportCases')}
            actions={[
                ...(window.carity.environment.shareSupportCases
                    ? [
                          <FormControlLabel
                              control={
                                  <Checkbox
                                      checked={filter.onlyMySupportCases}
                                      onChange={() => setFilter({ onlyMySupportCases: !filter.onlyMySupportCases })}
                                  />
                              }
                              label={t('showOnlyMySupportCases')}
                          />,
                      ]
                    : []),
                <SearchInput value={search} setValue={setSearch} />,
                ...(hasPermission(PermissionKeys.SUPPORT_CASES_WRITE)
                    ? [
                          <Button
                              color="primary"
                              variant="contained"
                              data-testid="newSupportCase"
                              component={Link}
                              to="/support/support-cases/new"
                          >
                              {t('newSupportCase')}
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <Table
                rows={supportCases?.data || []}
                columns={columns}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                rowCount={supportCases?.pagination?.size || 0}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
                onRowClick={({ row }) => navigate(`/support/support-cases/${row.id}/detail`)}
            />
        </Page>
    );
}
